import { z } from 'zod';
import { CityId } from './cities';
import { Gender, LifeGoal, Locale, NpcId, SKILL_MAX_LEVEL, SkillId, Stat, Trait, Weather } from './enums';

// ---------- auth ----------

export const DeviceInfo = z.object({
  deviceId: z.string().min(8).max(100),
  platform: z.enum(['ios', 'android', 'web']),
  appVersion: z.string().max(20),
});
export type DeviceInfo = z.infer<typeof DeviceInfo>;

export const GuestSignInRequest = z.object({ device: DeviceInfo });
export const EmailCodeRequest = z.object({ email: z.string().email().max(200) });
export const EmailVerifyRequest = z.object({
  email: z.string().email().max(200),
  code: z.string().regex(/^\d{6}$/),
  device: DeviceInfo,
});
export const IdTokenSignInRequest = z.object({
  provider: z.enum(['apple', 'google']),
  idToken: z.string().min(20).max(4096),
  nonce: z.string().max(200).optional(),
  device: DeviceInfo,
});
/** A one-time code (base64url of 32 random bytes) that signs the website in as the app's player. */
export const WebHandoffRedeemRequest = z.object({ code: z.string().regex(/^[A-Za-z0-9_-]{43}$/), device: DeviceInfo });
export type WebHandoffRedeemRequest = z.infer<typeof WebHandoffRedeemRequest>;

export const RefreshRequest = z.object({ refreshToken: z.string().min(20).max(400) });

export const AuthTokens = z.object({
  accessToken: z.string(),
  accessTokenExpiresAt: z.string(),
  refreshToken: z.string(),
  userId: z.string(),
  isGuest: z.boolean(),
});
export type AuthTokens = z.infer<typeof AuthTokens>;

// ---------- me ----------

export const AgeGateRequest = z.object({
  /** No longer asked (age is left to the store rating and the terms); older app builds still send it. */
  confirmedAdult: z.literal(true).optional(),
  termsVersion: z.string().max(20),
  aiProcessingConsent: z.literal(true),
});

export const Preferences = z.object({
  locale: Locale,
  /** The home companion (看板娘) the player chose; null lets the game pick the closest character. */
  homeNpcId: NpcId.nullable(),
  textScale: z.number().min(0.8).max(1.6),
  reduceMotion: z.boolean(),
  sound: z.boolean(),
  haptics: z.boolean(),
  npcNotifications: z.boolean(),
  playerNotifications: z.boolean(),
  quietHours: z.object({ start: z.number().int().min(0).max(23), end: z.number().int().min(0).max(23) }).nullable(),
  timezone: z.string().max(60),
  /** Home city: sets the game clock (real local time) and the real weather. */
  city: CityId,
  /** Page turns in the manga reader: 'ltr' swipes left for the next page, 'rtl' reads like a Japanese tankōbon. */
  readingDirection: z.enum(['ltr', 'rtl']),
});
export type Preferences = z.infer<typeof Preferences>;
export const PreferencesPatch = Preferences.partial();

export const MeResponse = z.object({
  userId: z.string(),
  publicId: z.string(),
  displayName: z.string().nullable(),
  isGuest: z.boolean(),
  email: z.string().nullable(),
  ageGatePassed: z.boolean(),
  hasCharacter: z.boolean(),
  preferences: Preferences,
  plus: z.object({ active: z.boolean(), renewsAt: z.string().nullable() }),
  unlocked: z.object({ people: z.boolean(), messages: z.boolean(), social: z.boolean(), training: z.boolean() }),
  createdAt: z.string(),
});
export type MeResponse = z.infer<typeof MeResponse>;

// ---------- character ----------

/** Presets and outfits are content ids (packages/content) with pre-approved art; the server validates them. */
export const Appearance = z.object({
  preset: z.string().regex(/^(male|female)_[a-z0-9_]{1,30}$/),
  outfit: z.string().regex(/^[a-z0-9_]{1,40}$/),
});
export type Appearance = z.infer<typeof Appearance>;

export const CreateCharacterRequest = z.object({
  name: z.string().trim().min(1).max(16),
  gender: Gender,
  appearance: Appearance,
  trait: Trait,
  lifeGoal: LifeGoal,
});
export type CreateCharacterRequest = z.infer<typeof CreateCharacterRequest>;

export const StatView = z.object({
  stat: Stat,
  value: z.number().int(),
  xp: z.number().int(),
  xpToNext: z.number().int(),
  /** About how many practices (at today's best activity, grade B) until the next point. */
  sessionsToNext: z.number().int().nullable(),
  /** Where this stat matters: choices in stories you haven't finished that roll on it, with one example. */
});
export const SkillView = z.object({
  skill: SkillId,
  level: z.number().int().min(0).max(SKILL_MAX_LEVEL),
  xp: z.number().int(),
  xpToNext: z.number().int().nullable(),
  /** About how many practices until the next level; null at max or when nothing trains it. */
  sessionsToNext: z.number().int().nullable(),
  unlockedEffects: z.array(z.string()),
  /** The skill's card art (a manga panel), served from the art store so it can change without a release. */
  imageUrl: z.string(),
});

export const CharacterView = z.object({
  id: z.string(),
  name: z.string(),
  gender: Gender,
  appearance: Appearance,
  appearanceVersion: z.number().int(),
  trait: Trait,
  lifeGoal: LifeGoal,
  portraitUrl: z.string().nullable(),
  /** Full-body art of the player's own character, for the Me hero. */
  figureUrl: z.string().nullable(),
  stats: z.array(StatView),
  skills: z.array(SkillView),
  titles: z.array(z.string()),
  coins: z.number().int(),
  clock: z.object({ gameDay: z.number().int(), slot: z.enum(['morning', 'afternoon', 'evening']), weather: Weather }),
});
export type CharacterView = z.infer<typeof CharacterView>;

export type GuestSignInRequest = z.infer<typeof GuestSignInRequest>;
export type EmailCodeRequest = z.infer<typeof EmailCodeRequest>;
export type EmailVerifyRequest = z.infer<typeof EmailVerifyRequest>;
export type IdTokenSignInRequest = z.infer<typeof IdTokenSignInRequest>;
export type RefreshRequest = z.infer<typeof RefreshRequest>;
export type AgeGateRequest = z.infer<typeof AgeGateRequest>;
export type PreferencesPatch = z.infer<typeof PreferencesPatch>;
